import {
    ActiveClock,
    attributionFromUrl,
    BrowserValueStore,
    createId,
    detectClientContext,
    EventBuffer,
    IdentityManager,
    sanitizeUrl,
    SDK_VERSION,
    trackingIsAllowed,
} from './core.js';
import { EngagementMonitor } from './engagement.js';
import { ResourceMonitor } from './resources.js';
import { FormTrackingManager } from './forms.js';

class TrackingTransport {
    constructor(browser, apiUrl) {
        this.browser = browser;
        this.apiUrl = apiUrl;
    }

    async send(payload, preferBeacon = false) {
        const body = JSON.stringify(payload);

        if (preferBeacon && typeof this.browser.navigator?.sendBeacon === 'function') {
            const accepted = this.browser.navigator.sendBeacon(
                this.apiUrl,
                new Blob([body], { type: 'application/json' }),
            );

            if (accepted) {
                return;
            }
        }

        const response = await this.browser.fetch(this.apiUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body,
            credentials: 'omit',
            keepalive: true,
        });

        if (! response.ok) {
            const error = new Error(`Tracking request failed with status ${response.status}.`);
            error.status = response.status;
            // Validation failures will not succeed on retry with the same payload.
            error.unrecoverable = response.status === 422 || response.status === 413;

            throw error;
        }
    }
}

class TrackingClient {
    constructor(browser, config) {
        const normalized = normalizeConfiguration(config, browser) ?? config;

        this.browser = browser;
        this.config = normalized;
        this.valueStore = new BrowserValueStore(browser, normalized.storeId);
        this.identityManager = new IdentityManager(
            this.valueStore,
            normalized.sessionTimeoutMinutes,
            () => Date.now(),
            browser.crypto,
        );
        this.eventBuffer = new EventBuffer(browser, normalized.storeId);
        this.transport = new TrackingTransport(browser, normalized.apiUrl);
        this.visitorId = this.identityManager.visitorId();
        this.session = this.identityManager.session();
        this.syncIdentityState();
        this.flushInProgress = false;
        this.flushTimer = null;
        this.flushPreferBeacon = false;
        this.retryAttempt = 0;
        this.retryTimer = null;
        this.navigationScheduled = false;
        this.storageListenerInstalled = false;
        this.lastTrackedClickKey = null;
        this.lastTrackedClickAt = 0;
        this.softBlocked = false;
        this.trackingStarted = false;
        this.blockStatusTimer = null;
        this.resetPage(browser.document.referrer);
        this.initialPageViewId = this.pageViewId;
        this.clientContext = detectClientContext(browser);
        this.largestContentfulPaintMs = null;
        this.resourceMonitor = null;
        this.lastResourceSnapshotSignature = null;
        this.formTrackingManager = new FormTrackingManager(browser, (name, properties, options = {}) => {
            const flushImmediately = options.flush === true;
            this.track(name, properties, { ...options, category: 'form', flush: false });

            if (flushImmediately) {
                this.flush({ preferBeacon: options.preferBeacon === true });
            } else {
                this.scheduleFlush();
            }
        }, {
            debug: normalized.debug,
            createEventId: () => createId('e', browser.crypto),
        });
    }

    start() {
        if (this.config.blockStatusUrl) {
            this.checkBlockStatus().then((blocked) => {
                if (! blocked) {
                    this.startTracking();
                }
            }).catch(() => this.startTracking());

            return;
        }

        this.startTracking();
    }

    startTracking() {
        if (this.trackingStarted || this.softBlocked) {
            return;
        }

        this.trackingStarted = true;
        this.enqueue('page_view', this.pageData());
        this.installListeners();

        if (this.config.trackJavascriptErrors !== false) {
            this.installJavascriptErrorTracking();
        }

        if (this.config.trackNetworkErrors !== false || this.config.trackNetworkRequests) {
            this.installNetworkInstrumentation();
        }

        if (this.config.trackPageResources !== false) {
            this.installResourceMonitoring();
        }

        if (this.config.trackPerformance !== false) {
            this.installPerformanceTracking();
        }

        if (this.config.trackSpaNavigation !== false) {
            this.installSpaNavigation();
        }

        if (this.config.formConfigUrl) {
            this.loadFormTrackingConfiguration();
        }

        if (this.config.sessionReplay) {
            this.safely(() => this.startSessionReplay());
        }

        this.flush();
        this.schedulePageResourceCapture();

        this.heartbeatTimer = this.browser.setInterval(() => {
            this.safely(() => {
                if (this.browser.document?.visibilityState === 'hidden') {
                    return;
                }

                this.enqueueUpdate(false);
                this.enqueuePageResources();
                this.flush();
            });
        }, this.config.heartbeatMilliseconds);

        if (this.config.blockStatusUrl) {
            this.blockStatusTimer = this.browser.setInterval(() => {
                this.safely(() => this.checkBlockStatus());
            }, Math.max(60000, this.config.heartbeatMilliseconds));
        }
    }

    async checkBlockStatus() {
        if (! this.config.blockStatusUrl) {
            return false;
        }

        const statusUrl = new URL(this.config.blockStatusUrl, this.browser.location.href);
        statusUrl.searchParams.set('visitor_id', this.visitorId);

        try {
            const response = await this.browser.fetch(statusUrl.toString(), {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'omit',
                cache: 'no-store',
            });

            if (! response?.ok) {
                return false;
            }

            const payload = await response.json();
            const blocked = payload?.data?.blocked === true;

            if (blocked) {
                this.applySoftBlock();
            }

            return blocked;
        } catch {
            return false;
        }
    }

    applySoftBlock() {
        if (this.softBlocked) {
            return;
        }

        this.softBlocked = true;
        this.eventBuffer.clear();
        this.formTrackingManager.stop(false);
        this.sessionReplay?.stop?.();

        if (this.heartbeatTimer) {
            this.browser.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }

        if (this.retryTimer !== null) {
            this.browser.clearTimeout?.(this.retryTimer);
            this.retryTimer = null;
        }

        this.cancelScheduledFlush();

        this.browser.AILogTrackerStatus = 'blocked_by_admin';
        this.renderBlockedOverlay();
    }

    renderBlockedOverlay() {
        const document = this.browser.document;

        if (! document?.createElement || document.getElementById?.('ailog-access-blocked')) {
            return;
        }

        const overlay = document.createElement('div');
        const panel = document.createElement('div');
        const title = document.createElement('h1');
        const message = document.createElement('p');

        overlay.id = 'ailog-access-blocked';
        overlay.setAttribute('role', 'alert');
        overlay.setAttribute('aria-live', 'assertive');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:24px;background:#0f172a;color:#e2e8f0;font-family:system-ui,-apple-system,sans-serif;';
        panel.style.cssText = 'width:min(100%,520px);padding:32px;border:1px solid #334155;border-radius:16px;background:#111827;text-align:center;box-shadow:0 24px 80px rgba(0,0,0,.45);';
        title.style.cssText = 'margin:0;font-size:28px;line-height:1.2;font-weight:700;';
        message.style.cssText = 'margin:14px 0 0;color:#94a3b8;font-size:16px;line-height:1.6;';
        title.textContent = 'Access denied';
        message.textContent = 'Your access to this website has been blocked by the administrator.';
        panel.append(title, message);
        overlay.append(panel);
        document.body?.append?.(overlay);

        if (document.body?.style) {
            document.body.style.overflow = 'hidden';
        }
    }

    installResourceMonitoring() {
        this.resourceMonitor = new ResourceMonitor(this.browser, {
            isCollectorUrl: (url) => this.isCollectorUrl(url),
        });
        this.resourceMonitor.install(Date.now());
    }

    async loadFormTrackingConfiguration() {
        try {
            const response = await this.browser.fetch(this.config.formConfigUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'omit',
            });

            if (! response?.ok) {
                return;
            }

            const payload = await response.json();
            this.formTrackingManager.setConfigurations(payload?.data ?? []);
            this.formTrackingManager.activate(this.browser.location.href, this.pageViewId);
        } catch {
            this.formTrackingManager.log('Configuration could not be loaded.');
        }
    }

    schedulePageResourceCapture() {
        if (this.config.trackPageResources === false || ! this.resourceMonitor) {
            return;
        }

        const capture = () => {
            if (this.pageEnded) {
                return;
            }

            this.enqueuePageResources();
            this.scheduleFlush();
        };

        this.browser.setTimeout?.(() => this.safely(capture), 3000);
    }

    startSessionReplay() {
        const replayApiUrl = this.config.replayApiUrl
            || this.config.apiUrl.replace(/\/batch(?:\?.*)?$/, '/replay');
        const replayScriptUrl = this.config.replayScriptUrl
            || this.deriveReplayScriptUrl();

        this.loadReplayModule(replayScriptUrl).then((api) => {
            if (! api || typeof api.start !== 'function') {
                return;
            }

            this.sessionReplay = api.start(this.browser, {
                storeId: this.config.storeId,
                apiUrl: replayApiUrl,
                visitorId: this.visitorId,
                sessionId: this.session.id,
                valueStore: this.valueStore,
            });
        }).catch(() => undefined);
    }

    deriveReplayScriptUrl() {
        const script = resolveTrackerScript(this.browser);
        const source = scriptSource(script);

        if (source) {
            try {
                const replayUrl = new URL(source, this.browser.location.href);
                replayUrl.pathname = replayUrl.pathname.replace(/tracker\.js$/i, 'tracker-replay.js');
                replayUrl.search = `?v=${SDK_VERSION}`;

                return replayUrl.toString();
            } catch {
                // Fall through to relative default.
            }
        }

        return new URL(`tracker-replay.js?v=${SDK_VERSION}`, this.browser.location.href).toString();
    }

    loadReplayModule(scriptUrl) {
        if (this.browser.AILogSessionReplay) {
            return Promise.resolve(this.browser.AILogSessionReplay);
        }

        if (this.replayModulePromise) {
            return this.replayModulePromise;
        }

        this.replayModulePromise = new Promise((resolve, reject) => {
            const script = this.browser.document.createElement('script');
            script.async = true;
            script.src = scriptUrl;
            script.onload = () => resolve(this.browser.AILogSessionReplay);
            script.onerror = () => reject(new Error('Failed to load session replay module.'));
            this.browser.document.head.appendChild(script);
        });

        return this.replayModulePromise;
    }

    installListeners() {
        this.browser.document.addEventListener('visibilitychange', () => this.safely(() => {
            if (this.pageEnded) {
                return;
            }

            const visible = this.browser.document.visibilityState !== 'hidden';
            this.clock.setVisible(visible);
            this.enqueueUpdate(false);
            this.flush({ preferBeacon: ! visible });
        }), { passive: true });

        this.browser.addEventListener('pagehide', () => this.safely(() => this.endPage()), { capture: true });
        this.browser.addEventListener('beforeunload', () => this.safely(() => this.endPage()), { capture: true });
        this.browser.addEventListener('online', () => this.safely(() => this.flush()), { passive: true });
        this.browser.addEventListener('scroll', () => this.safely(() => {
            this.captureScrollDepth();
            this.engagement?.onScroll();
        }), { passive: true });
        this.browser.addEventListener('mousemove', () => this.safely(() => this.engagement?.onInteraction()), { passive: true });
        this.browser.addEventListener('keydown', () => this.safely(() => this.engagement?.onInteraction()), { passive: true });
        this.browser.addEventListener('touchstart', () => this.safely(() => this.engagement?.onInteraction()), { passive: true });
        this.browser.document?.addEventListener?.('click', (event) => {
            this.safely(() => {
                const heatmapTracked = this.handleHeatmapClick(event);
                const uiTracked = this.handleUiClick(event);

                if (heatmapTracked || uiTracked) {
                    this.scheduleFlush(2500, true);
                }
            });
        }, true);
        this.installStorageListener();
    }

    handleHeatmapClick(event) {
        if (this.config.trackHeatmaps === false) {
            return false;
        }

        const target = event?.target;

        if (target?.closest?.('[data-ailog-ignore]')) {
            return false;
        }

        const pageX = Number.isFinite(event?.pageX)
            ? Number(event.pageX)
            : Number(event?.clientX) + Number(this.browser.scrollX || 0);
        const pageY = Number.isFinite(event?.pageY)
            ? Number(event.pageY)
            : Number(event?.clientY) + Number(this.browser.scrollY || 0);
        const documentElement = this.browser.document?.documentElement;
        const body = this.browser.document?.body;
        const documentWidth = Math.max(
            Number(documentElement?.scrollWidth || 0),
            Number(documentElement?.clientWidth || 0),
            Number(body?.scrollWidth || 0),
            Number(body?.clientWidth || 0),
            Number(this.browser.innerWidth || 0),
        );
        const documentHeight = Math.max(
            Number(documentElement?.scrollHeight || 0),
            Number(documentElement?.clientHeight || 0),
            Number(body?.scrollHeight || 0),
            Number(body?.clientHeight || 0),
            Number(this.browser.innerHeight || 0),
        );

        if (! Number.isFinite(pageX) || ! Number.isFinite(pageY) || documentWidth <= 0 || documentHeight <= 0) {
            return false;
        }

        this.track('heatmap_click', {
            x_percent: Math.round(Math.min(100, Math.max(0, (pageX / documentWidth) * 100)) * 100) / 100,
            y_percent: Math.round(Math.min(100, Math.max(0, (pageY / documentHeight) * 100)) * 100) / 100,
            viewport_width: Math.max(0, Math.round(this.browser.innerWidth || 0)),
            viewport_height: Math.max(0, Math.round(this.browser.innerHeight || 0)),
            document_width: Math.round(documentWidth),
            document_height: Math.round(documentHeight),
            element_tag: this.text(target?.tagName || '', 30).toLowerCase() || 'unknown',
        }, {
            flush: false,
            preferBeacon: true,
        });

        return true;
    }

    handleUiClick(event) {
        const target = event?.target;

        if (! target || typeof target.closest !== 'function') {
            return;
        }

        if (target.closest?.('[data-ailog-ignore]')) {
            return;
        }

        const element = target.closest([
            '[data-ailog-track]',
            'button',
            'input[type="button"]',
            'input[type="submit"]',
            'input[type="image"]',
            '[role="button"]',
            'a.add-to-cart',
            '.add-to-cart',
            '.new-add-to-cart',
        ].join(', '));

        if (! element || element.disabled || element.getAttribute?.('aria-disabled') === 'true') {
            return;
        }

        if (this.config.trackButtonClicks === false && ! element.matches?.('[data-ailog-track]')) {
            return;
        }

        const clickKey = this.clickableKey(element);
        const now = Date.now();

        if (clickKey && clickKey === this.lastTrackedClickKey && now - this.lastTrackedClickAt < 500) {
            return;
        }

        this.lastTrackedClickKey = clickKey;
        this.lastTrackedClickAt = now;

        const label = this.clickableLabel(element);
        const trackAction = String(element.getAttribute?.('data-ailog-track') || '').trim().toLowerCase();
        const isAddToCart = trackAction === 'add_to_cart' || this.isAddToCartElement(element, label);
        let shouldFlush = false;

        if (isAddToCart) {
            this.addToCart(this.productFromTrackElement(element), {
                flush: false,
                preferBeacon: true,
            });
            shouldFlush = true;
        } else if (trackAction !== '' && trackAction !== 'add_to_cart') {
            this.track(trackAction, this.sanitizeTrackProperties({
                label,
                id: element.id || null,
                name: element.getAttribute?.('name'),
                classes: this.clickableClasses(element),
            }), {
                flush: false,
                preferBeacon: true,
            });
            shouldFlush = true;
        }

        if (this.config.trackButtonClicks !== false) {
            this.track('button_clicked', {
                label,
                id: element.id || null,
                name: element.getAttribute?.('name'),
                classes: this.clickableClasses(element),
                tag: String(element.tagName || '').toLowerCase(),
                type: element.getAttribute?.('type') || element.type || null,
                value: this.text(element.getAttribute?.('value') || element.value || '', 120) || null,
                href: this.text(element.getAttribute?.('href') || '', 500) || null,
                is_add_to_cart: isAddToCart,
            }, {
                flush: false,
                preferBeacon: true,
            });
            shouldFlush = true;
        }

        return shouldFlush;
    }

    clickableKey(element) {
        return [
            element.tagName,
            element.id,
            element.getAttribute?.('name'),
            this.clickableClasses(element),
            this.clickableLabel(element),
        ].join('|');
    }

    clickableClasses(element) {
        if (typeof element.className === 'string') {
            return this.text(element.className, 200) || null;
        }

        if (typeof element.getAttribute === 'function') {
            return this.text(element.getAttribute('class') || '', 200) || null;
        }

        return null;
    }

    clickableLabel(element) {
        const tag = String(element.tagName || '').toLowerCase();
        const candidates = tag === 'input'
            ? [
                element.getAttribute?.('aria-label'),
                element.getAttribute?.('title'),
                element.getAttribute?.('alt'),
                element.getAttribute?.('value'),
                element.value,
                element.getAttribute?.('name'),
            ]
            : [
                element.getAttribute?.('aria-label'),
                element.getAttribute?.('title'),
                element.innerText,
                element.textContent,
                element.getAttribute?.('value'),
                element.value,
                element.getAttribute?.('alt'),
                element.getAttribute?.('name'),
            ];

        for (const candidate of candidates) {
            const label = this.text(String(candidate ?? '').replace(/\s+/g, ' '), 120);

            if (label) {
                return label;
            }
        }

        return tag || 'button';
    }

    isAddToCartElement(element, label = '') {
        const haystack = [
            label,
            this.clickableClasses(element),
            element.getAttribute?.('name'),
            element.getAttribute?.('id'),
            element.getAttribute?.('value'),
            element.id,
        ].filter(Boolean).join(' ').toLowerCase();

        if (element.classList?.contains?.('add-to-cart') || element.classList?.contains?.('new-add-to-cart')) {
            return true;
        }

        return /add[\s_-]*to[\s_-]*cart|addtocart/.test(haystack);
    }

    productFromTrackElement(element) {
        const attr = (name) => {
            const value = element.getAttribute?.(name);

            return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
        };

        const quantitySelector = attr('data-ailog-quantity-selector');
        let quantity = this.numberFromAttr(attr('data-ailog-quantity'));

        if (quantity === null && quantitySelector) {
            quantity = this.quantityFromSelector(quantitySelector);
        }

        if (quantity === null) {
            quantity = this.quantityFromSelector('#qtybox');
        }

        return {
            productId: attr('data-ailog-product-id') ?? attr('data-ailog-sku'),
            name: attr('data-ailog-product-name'),
            sku: attr('data-ailog-sku'),
            price: this.numberFromAttr(attr('data-ailog-price')),
            currency: attr('data-ailog-currency') ?? 'USD',
            quantity: quantity ?? 1,
            category: attr('data-ailog-category'),
            brand: attr('data-ailog-brand'),
            variant: attr('data-ailog-variant'),
        };
    }

    numberFromAttr(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const number = Number(String(value).replace(/[^0-9.-]/g, ''));

        return Number.isFinite(number) ? number : null;
    }

    quantityFromSelector(selector) {
        try {
            const field = this.browser.document?.querySelector?.(selector);

            if (! field) {
                return null;
            }

            return this.numberFromAttr(field.value ?? field.getAttribute?.('value'));
        } catch {
            return null;
        }
    }

    installStorageListener() {
        if (this.storageListenerInstalled || typeof this.browser.addEventListener !== 'function') {
            return;
        }

        this.storageListenerInstalled = true;
        this.browser.addEventListener('storage', (event) => {
            const sessionKey = this.valueStore.storageKey('session');
            const visitorKey = this.valueStore.storageKey('visitor');

            if (event.key !== sessionKey && event.key !== visitorKey) {
                return;
            }

            this.safely(() => this.adoptStoredIdentity());
        });
    }

    adoptStoredIdentity() {
        this.syncIdentityState();
    }

    syncIdentityState() {
        const previousVisitorId = this.visitorId;
        const previousSessionId = this.session.id;

        this.visitorId = this.identityManager.visitorId();
        this.identityManager.touch(this.session);

        if (previousVisitorId !== this.visitorId) {
            this.eventBuffer.dropMismatched(this.visitorId, this.session.id);
        } else if (previousSessionId !== this.session.id) {
            this.eventBuffer.alignSession(this.visitorId, this.session.id);
        }
    }

    installJavascriptErrorTracking() {
        this.browser.addEventListener('error', (event) => this.safely(() => {
            const error = event?.error;

            if (! error && ! event?.message) {
                return;
            }

            this.enqueueDiagnostic('javascript_error', {
                error_message: this.text(error?.message ?? event?.message ?? 'Unknown JavaScript error', 2000),
                error_type: this.text(error?.name ?? 'Error', 100),
                filename: this.sourceUrl(event?.filename),
                line_number: this.unsignedInteger(event?.lineno),
                column_number: this.unsignedInteger(event?.colno),
                stack_trace: this.text(error?.stack, 16000),
                page_url: this.currentPageUrl(),
            });
        }), { capture: true });

        this.browser.addEventListener('unhandledrejection', (event) => this.safely(() => {
            const reason = event?.reason;
            const isErrorLike = reason !== null && (typeof reason === 'object' || typeof reason === 'function');
            const message = isErrorLike ? reason.message : reason;

            this.enqueueDiagnostic('javascript_error', {
                error_message: this.text(message ?? 'Unhandled promise rejection', 2000),
                error_type: this.text(isErrorLike ? (reason.name ?? 'UnhandledRejection') : 'UnhandledRejection', 100),
                filename: null,
                line_number: null,
                column_number: null,
                stack_trace: this.text(isErrorLike ? reason.stack : null, 16000),
                page_url: this.currentPageUrl(),
            });
        }), { capture: true });
    }

    installNetworkInstrumentation() {
        this.instrumentFetch();
        this.instrumentXmlHttpRequest();
    }

    installNetworkErrorTracking() {
        this.installNetworkInstrumentation();
    }

    installNetworkRequestTracking() {
        this.installNetworkInstrumentation();
    }

    installPerformanceTracking() {
        const PerformanceObserver = this.browser.PerformanceObserver;

        if (typeof PerformanceObserver === 'function') {
            this.safely(() => {
                this.performanceObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries?.() ?? [];
                    const latest = entries.at(-1);
                    const value = latest?.renderTime || latest?.loadTime || latest?.startTime;

                    if (Number.isFinite(value)) {
                        this.largestContentfulPaintMs = this.timingMetric(value);
                    }
                });
                this.performanceObserver.observe({ type: 'largest-contentful-paint', buffered: true });
            });
        }

        const scheduleCollection = () => this.browser.setTimeout(
            () => this.safely(() => this.collectPerformance()),
            0,
        );

        if (this.browser.document.readyState === 'complete') {
            scheduleCollection();
        } else {
            this.browser.addEventListener('load', scheduleCollection, { once: true });
        }
    }

    collectPerformance() {
        const performance = this.browser.performance;

        if (! performance || typeof performance.getEntriesByType !== 'function') {
            return;
        }

        const navigation = performance.getEntriesByType('navigation')?.[0];
        const firstContentfulPaint = performance.getEntriesByType('paint')
            ?.find((entry) => entry.name === 'first-contentful-paint');
        const resources = performance.getEntriesByType('resource') ?? [];
        const lastResourceEnd = resources.reduce(
            (latest, entry) => Math.max(latest, Number(entry?.responseEnd) || 0),
            0,
        );
        const timings = {
            page_load_ms: this.durationMetric(navigation?.loadEventEnd, navigation?.startTime),
            dom_content_loaded_ms: this.durationMetric(navigation?.domContentLoadedEventEnd, navigation?.startTime),
            first_contentful_paint_ms: this.timingMetric(firstContentfulPaint?.startTime),
            largest_contentful_paint_ms: this.largestContentfulPaintMs,
            time_to_first_byte_ms: this.durationMetric(navigation?.responseStart, navigation?.requestStart ?? navigation?.startTime),
            resource_load_ms: this.timingMetric(lastResourceEnd || null),
        };

        if (! Object.values(timings).some((value) => value !== null)) {
            return;
        }

        this.enqueue('performance', { page_view_id: this.initialPageViewId, ...timings });
        this.scheduleFlush();
        this.performanceObserver?.disconnect?.();
    }

    durationMetric(end, start = 0) {
        if (! Number.isFinite(Number(end)) || ! Number.isFinite(Number(start)) || Number(end) <= 0) {
            return null;
        }

        return this.timingMetric(Number(end) - Number(start));
    }

    timingMetric(value) {
        const number = Number(value);

        return Number.isFinite(number) && number >= 0
            ? Math.max(0, Math.min(600000, Math.round(number)))
            : null;
    }

    instrumentFetch() {
        if (this.fetchInstrumented) {
            return;
        }

        const originalFetch = this.browser.fetch;

        if (typeof originalFetch !== 'function') {
            return;
        }

        const client = this;

        try {
            this.fetchInstrumented = true;
            this.browser.fetch = function (...args) {
                const request = client.fetchMetadata(args[0], args[1]);
                const requestInit = args[1] ?? {};
                const startedAt = client.monotonicNow();
                const monitorRequest = client.shouldMonitorNetworkRequest(request);
                const trackErrors = client.config.trackNetworkErrors !== false;
                let promise;

                try {
                    promise = originalFetch.apply(this, args);
                } catch (error) {
                    if (monitorRequest) {
                        client.captureNetworkRequest(request, null, startedAt, requestInit, args[0]);
                    }

                    if (trackErrors) {
                        client.recordNetworkError(request, null, 'network_error', startedAt);
                    }

                    throw error;
                }

                if (! request.ignored && promise && typeof promise.then === 'function') {
                    promise.then(
                        (response) => {
                            if (monitorRequest) {
                                client.captureNetworkRequest(request, response, startedAt, requestInit, args[0]);
                            }

                            if (trackErrors && Number(response?.status) >= 400) {
                                client.recordNetworkError(request, response.status, 'http_error', startedAt);
                            }

                            client.formTrackingManager.notifyNetwork(request, Number(response?.status) || null);
                        },
                        () => {
                            if (monitorRequest) {
                                client.captureNetworkRequest(request, null, startedAt, requestInit, args[0]);
                            }

                            if (trackErrors) {
                                client.recordNetworkError(request, null, 'network_error', startedAt);
                            }

                            client.formTrackingManager.notifyNetwork(request, null, 'network_error');
                        },
                    );
                }

                return promise;
            };
        } catch {
            // A locked fetch property only disables request instrumentation.
        }
    }

    instrumentXmlHttpRequest() {
        if (this.xmlHttpRequestInstrumented) {
            return;
        }

        const prototype = this.browser.XMLHttpRequest?.prototype;

        if (! prototype || typeof prototype.open !== 'function' || typeof prototype.send !== 'function') {
            return;
        }

        const client = this;
        const requests = new WeakMap();
        const xhrHeaders = new WeakMap();
        const originalOpen = prototype.open;
        const originalSend = prototype.send;
        const originalSetRequestHeader = prototype.setRequestHeader;

        try {
            this.xmlHttpRequestInstrumented = true;
            if (typeof originalSetRequestHeader === 'function') {
                prototype.setRequestHeader = function (name, value) {
                    const headers = xhrHeaders.get(this) ?? {};
                    headers[name] = value;
                    xhrHeaders.set(this, headers);

                    return originalSetRequestHeader.call(this, name, value);
                };
            }

            prototype.open = function (method, url, ...args) {
                requests.set(this, client.requestMetadata(method, url));
                xhrHeaders.set(this, {});

                return originalOpen.call(this, method, url, ...args);
            };

            prototype.send = function (...args) {
                const request = requests.get(this) ?? client.requestMetadata('GET', '');
                const startedAt = client.monotonicNow();
                const monitorRequest = client.shouldMonitorNetworkRequest(request);
                const trackErrors = client.config.trackNetworkErrors !== false;
                const requestBody = args[0];
                let recorded = false;
                const record = (errorType) => {
                    if (recorded || request.ignored || ! trackErrors) {
                        return;
                    }

                    recorded = true;
                    client.recordNetworkError(request, Number(this.status) || null, errorType, startedAt);
                };

                this.addEventListener?.('error', () => record('network_error'), { once: true });
                this.addEventListener?.('abort', () => record('aborted'), { once: true });
                this.addEventListener?.('timeout', () => record('timeout'), { once: true });
                this.addEventListener?.('loadend', () => {
                    if (monitorRequest) {
                        client.captureXmlHttpRequest(request, this, startedAt, requestBody, xhrHeaders.get(this) ?? {});
                    }

                    if (Number(this.status) >= 400) {
                        record('http_error');
                    }

                    client.formTrackingManager.notifyNetwork(request, Number(this.status) || null);
                }, { once: true });

                try {
                    return originalSend.apply(this, args);
                } catch (error) {
                    if (monitorRequest) {
                        client.captureXmlHttpRequest(request, this, startedAt, requestBody, xhrHeaders.get(this) ?? {}, true);
                    }

                    record('network_error');
                    throw error;
                }
            };
        } catch {
            // Locked XMLHttpRequest methods only disable request instrumentation.
        }
    }

    fetchMetadata(input, init) {
        const method = init?.method ?? (input && typeof input === 'object' ? input.method : 'GET');
        const url = typeof input === 'string' || input instanceof URL ? input.toString() : input?.url;

        return this.requestMetadata(method, url);
    }

    requestMetadata(method, url) {
        const requestUrl = sanitizeUrl(url, this.browser.location.href);

        return {
            method: this.text(method ?? 'GET', 10).toUpperCase() || 'GET',
            url: requestUrl,
            ignored: ! requestUrl || this.isCollectorUrl(requestUrl),
        };
    }

    isCollectorUrl(requestUrl) {
        for (const collectorUrl of this.collectorUrls()) {
            if (requestUrl === collectorUrl) {
                return true;
            }
        }

        try {
            const pathname = new URL(requestUrl).pathname;

            if (/\/api\/v1\/track(?:\/|$)/i.test(pathname)) {
                return true;
            }

            if (/\/tracker(?:-replay)?\.js$/i.test(pathname)) {
                return true;
            }
        } catch {
            // Ignore malformed absolute URLs after sanitization.
        }

        return false;
    }

    collectorUrls() {
        if (this.cachedCollectorUrls) {
            return this.cachedCollectorUrls;
        }

        const base = this.browser.location.href;
        const batchUrl = sanitizeUrl(this.config.apiUrl, base);
        const replayUrl = sanitizeUrl(
            this.config.replayApiUrl
                || String(this.config.apiUrl || '').replace(/\/batch(?:\?.*)?$/i, '/replay'),
            base,
        );

        this.cachedCollectorUrls = [batchUrl, replayUrl].filter(Boolean);

        return this.cachedCollectorUrls;
    }

    recordNetworkError(request, statusCode, errorType, startedAt) {
        if (request.ignored) {
            return;
        }

        this.safely(() => this.enqueueDiagnostic('network_error', {
            request_url: request.url,
            method: request.method,
            status_code: statusCode,
            response_time_ms: Math.max(0, Math.min(600000, Math.round(this.monotonicNow() - startedAt))),
            error_type: errorType,
            page_url: this.currentPageUrl(),
        }));
    }

    shouldMonitorNetworkRequest(request) {
        if (! this.config.trackNetworkRequests || request.ignored) {
            return false;
        }

        try {
            const pathname = new URL(request.url).pathname;

            return this.config.networkRequestPatterns.some((pattern) => pattern.test(pathname));
        } catch {
            return false;
        }
    }

    captureNetworkRequest(request, response, startedAt, init = {}, input = null) {
        const responseTime = Math.max(0, Math.min(600000, Math.round(this.monotonicNow() - startedAt)));
        const payload = {
            request_url: request.url,
            endpoint: this.endpointFromUrl(request.url),
            method: request.method,
            status_code: response ? Number(response.status) || null : null,
            response_time_ms: responseTime,
            response_size_bytes: response ? this.responseSizeFromHeaders(response.headers) : null,
            request_payload: this.captureRequestPayload(init?.body),
            request_headers: this.captureRequestHeaders(init?.headers, input),
            error_response: null,
            page_url: this.currentPageUrl(),
        };

        if (! response || Number(response.status) < 400) {
            this.safely(() => this.enqueueDiagnostic('network_request', payload));

            return;
        }

        this.safely(() => {
            const finalize = (errorResponse, responseSize) => {
                this.enqueueDiagnostic('network_request', {
                    ...payload,
                    error_response: errorResponse,
                    response_size_bytes: responseSize ?? payload.response_size_bytes,
                });
            };

            try {
                const cloned = response.clone();
                const textPromise = typeof cloned.text === 'function' ? cloned.text() : Promise.resolve('');

                Promise.resolve(textPromise).then((text) => {
                    finalize(this.text(text, 4096), payload.response_size_bytes ?? this.textByteLength(text));
                }).catch(() => finalize(null, payload.response_size_bytes));
            } catch {
                finalize(null, payload.response_size_bytes);
            }
        });
    }

    captureXmlHttpRequest(request, xhr, startedAt, requestBody, headers, failed = false) {
        const statusCode = failed ? null : Number(xhr.status) || null;
        let errorResponse = null;
        let responseSize = this.responseSizeFromHeaderString(xhr.getResponseHeader?.('content-length'));

        if (! failed && statusCode !== null && statusCode >= 400 && typeof xhr.responseText === 'string') {
            errorResponse = this.text(xhr.responseText, 4096);
            responseSize ??= this.textByteLength(xhr.responseText);
        }

        this.enqueueDiagnostic('network_request', {
            request_url: request.url,
            endpoint: this.endpointFromUrl(request.url),
            method: request.method,
            status_code: statusCode,
            response_time_ms: Math.max(0, Math.min(600000, Math.round(this.monotonicNow() - startedAt))),
            response_size_bytes: responseSize,
            request_payload: this.captureRequestPayload(requestBody),
            request_headers: this.capturePlainHeaders(headers),
            error_response: errorResponse,
            page_url: this.currentPageUrl(),
        });
    }

    endpointFromUrl(url) {
        try {
            return this.text(new URL(url).pathname || '/', 2048);
        } catch {
            return this.text(url, 2048);
        }
    }

    captureRequestPayload(body) {
        if (body === undefined || body === null || body === '') {
            return null;
        }

        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            const fields = [];

            for (const [key, value] of body.entries()) {
                if (this.isSensitiveKey(key)) {
                    fields.push(`${key}=[redacted]`);

                    continue;
                }

                fields.push(`${key}=${this.text(String(value), 120)}`);
            }

            return this.text(fields.join('&'), 4096);
        }

        if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
            return this.text(body.toString(), 4096);
        }

        if (typeof body === 'string') {
            return this.text(body, 4096);
        }

        try {
            return this.text(JSON.stringify(body), 4096);
        } catch {
            return null;
        }
    }

    captureRequestHeaders(headers, input = null) {
        const captured = {};

        this.mergeHeaders(captured, this.headersFromInit(headers));
        this.mergeHeaders(captured, this.headersFromRequest(input));

        return Object.keys(captured).length === 0 ? null : captured;
    }

    capturePlainHeaders(headers) {
        const captured = {};

        this.mergeHeaders(captured, headers);

        return Object.keys(captured).length === 0 ? null : captured;
    }

    headersFromInit(headers) {
        if (! headers) {
            return {};
        }

        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
            const normalized = {};

            headers.forEach((value, key) => {
                normalized[key] = value;
            });

            return normalized;
        }

        if (Array.isArray(headers)) {
            const normalized = {};

            for (const entry of headers) {
                if (Array.isArray(entry) && entry.length >= 2) {
                    normalized[entry[0]] = entry[1];
                }
            }

            return normalized;
        }

        return typeof headers === 'object' ? headers : {};
    }

    headersFromRequest(input) {
        if (! input || typeof input !== 'object' || ! input.headers) {
            return {};
        }

        return this.headersFromInit(input.headers);
    }

    mergeHeaders(target, source) {
        if (! source || typeof source !== 'object') {
            return;
        }

        for (const [key, value] of Object.entries(source)) {
            if (! key) {
                continue;
            }

            target[key] = this.isSensitiveKey(key) ? '[redacted]' : this.text(value, 500);
        }
    }

    isSensitiveKey(key) {
        const normalized = String(key).toLowerCase();

        return ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-auth-token', 'password', 'token'].includes(normalized);
    }

    responseSizeFromHeaders(headers) {
        if (! headers || typeof headers.get !== 'function') {
            return null;
        }

        return this.responseSizeFromHeaderString(headers.get('content-length'));
    }

    responseSizeFromHeaderString(value) {
        const number = Number(value);

        return Number.isFinite(number) && number >= 0
            ? Math.min(104857600, Math.round(number))
            : null;
    }

    textByteLength(value) {
        if (typeof value !== 'string' || value === '') {
            return null;
        }

        return Math.min(104857600, value.length);
    }

    enqueueDiagnostic(type, data) {
        if (! data.page_url) {
            return;
        }

        this.enqueue(type, { page_view_id: this.pageViewId, ...data });
        this.scheduleFlush(1000);
    }

    currentPageUrl() {
        return sanitizeUrl(this.browser.location.href, this.browser.location.href) ?? this.currentUrl;
    }

    monotonicNow() {
        return typeof this.browser.performance?.now === 'function'
            ? this.browser.performance.now()
            : Date.now();
    }

    text(value, maximumLength) {
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
            return '';
        }

        return String(value)
            .replace(/\bBearer\s+[^\s,;]+/gi, 'Bearer [redacted]')
            .replace(/((?:access_token|api_key|apikey|auth|authorization|code|email|id_token|jwt|password|passwd|secret|session|session_id|token)\s*[=:]\s*)[^&\s,;]+/gi, '$1[redacted]')
            .slice(0, maximumLength);
    }

    sourceUrl(value) {
        if (! value) {
            return null;
        }

        return sanitizeUrl(value, this.browser.location.href) ?? this.text(value, 4096);
    }

    unsignedInteger(value) {
        const number = Number(value);

        return Number.isFinite(number) ? Math.max(0, Math.min(4294967295, Math.round(number))) : null;
    }

    installSpaNavigation() {
        const history = this.browser.history;

        if (! history) {
            return;
        }

        for (const method of ['pushState', 'replaceState']) {
            const original = history[method];

            if (typeof original !== 'function') {
                continue;
            }

            try {
                const client = this;
                history[method] = function (...args) {
                    const result = original.apply(this, args);
                    client.scheduleNavigation();

                    return result;
                };
            } catch {
                // A locked History object only disables automatic SPA detection.
            }
        }

        this.browser.addEventListener('popstate', () => this.safely(() => this.scheduleNavigation()), { passive: true });
    }

    scheduleNavigation() {
        if (this.navigationScheduled || this.pageEnded) {
            return;
        }

        this.navigationScheduled = true;
        const schedule = typeof this.browser.queueMicrotask === 'function'
            ? this.browser.queueMicrotask.bind(this.browser)
            : (callback) => Promise.resolve().then(callback);

        try {
            schedule(() => {
                this.navigationScheduled = false;
                this.safely(() => this.handleNavigation());
            });
        } catch {
            this.navigationScheduled = false;
        }
    }

    handleNavigation() {
        const nextUrl = sanitizeUrl(this.browser.location.href, this.browser.location.href);

        if (! nextUrl || nextUrl === this.currentUrl) {
            return;
        }

        const previousUrl = this.currentUrl;
        this.formTrackingManager.abandonAll();
        this.finalizePage(false);
        this.resetPage(previousUrl);
        this.enqueue('page_view', this.pageData());
        this.flush();
        this.schedulePageResourceCapture();
        this.formTrackingManager.activate(this.browser.location.href, this.pageViewId);
    }

    endPage() {
        this.formTrackingManager.abandonAll();

        if (! this.finalizePage(true)) {
            return;
        }

        this.cancelScheduledFlush();
        this.flush({ preferBeacon: true });

        if (this.heartbeatTimer) {
            this.browser.clearInterval(this.heartbeatTimer);
        }
    }

    finalizePage(sessionEnded) {
        if (this.pageEnded) {
            return false;
        }

        this.pageEnded = true;
        this.clock.setVisible(false);
        this.captureScrollDepth();
        this.enqueueUpdate(true, sessionEnded);
        this.enqueuePageResources(true);

        return true;
    }

    resetPage(referrer) {
        this.pageViewId = createId('pv', this.browser.crypto);
        this.clock = new ActiveClock(Date.now(), this.browser.document.visibilityState !== 'hidden');
        this.maximumScrollDepth = 0;
        this.engagement = new EngagementMonitor(this.browser, this.clock);
        this.engagement.reset(Date.now());
        this.lastResourceSnapshotSignature = null;

        if (this.resourceMonitor) {
            this.resourceMonitor.reset(Date.now());
        } else if (this.config.trackPageResources !== false) {
            this.installResourceMonitoring();
        }

        this.currentUrl = sanitizeUrl(this.browser.location.href, this.browser.location.href);
        this.currentReferrer = sanitizeUrl(referrer, this.browser.location.href);
        this.pageEnded = false;
    }

    pageData() {
        const parsedUrl = this.currentUrl ? new URL(this.currentUrl) : null;
        const attribution = attributionFromUrl(this.browser.location.href, this.browser.location.href);

        for (const key of Object.keys(attribution)) {
            attribution[key] = this.text(attribution[key], 255) || null;
        }

        return {
            page_view_id: this.pageViewId,
            url: this.currentUrl,
            path: parsedUrl?.pathname ?? '/',
            query_string: parsedUrl?.search.slice(1) || null,
            page_title: this.browser.document.title?.slice(0, 500) || null,
            referrer: this.currentReferrer,
            viewport_width: Math.max(0, Math.round(this.browser.innerWidth || 0)),
            viewport_height: Math.max(0, Math.round(this.browser.innerHeight || 0)),
            screen_width: Math.max(0, Math.round(this.browser.screen?.width || 0)),
            screen_height: Math.max(0, Math.round(this.browser.screen?.height || 0)),
            scroll_depth: this.maximumScrollDepth,
            engagement_metrics: this.engagementMetrics(),
            ...this.clientContext,
            ...attribution,
        };
    }

    enqueueUpdate(exited, sessionEnded = false) {
        const duration = this.clock.snapshot();
        this.identityManager.touch(this.session);

        this.enqueue('page_update', {
            page_view_id: this.pageViewId,
            duration_seconds: duration.durationSeconds,
            active_duration_seconds: duration.activeDurationSeconds,
            viewport_width: Math.max(0, Math.round(this.browser.innerWidth || 0)),
            viewport_height: Math.max(0, Math.round(this.browser.innerHeight || 0)),
            scroll_depth: this.maximumScrollDepth,
            engagement_metrics: this.engagementMetrics(),
            exited,
            session_ended: sessionEnded,
        });
    }

    enqueuePageResources(force = false) {
        if (this.config.trackPageResources === false || ! this.resourceMonitor) {
            return;
        }

        const resources = this.resourceMonitor.snapshot();

        if (resources.length === 0) {
            return;
        }

        const signature = JSON.stringify(resources);

        if (! force && signature === this.lastResourceSnapshotSignature) {
            return;
        }

        this.lastResourceSnapshotSignature = signature;
        this.enqueue('page_resources', {
            page_view_id: this.pageViewId,
            resources,
        });
    }

    enqueue(type, data, eventId = null) {
        if (this.softBlocked) {
            return;
        }

        if (type === 'page_view' && ! data.url) {
            return;
        }

        this.syncIdentityState();

        this.eventBuffer.add(this.visitorId, this.session.id, {
            id: /^e_[A-Za-z0-9_-]{16,64}$/.test(eventId ?? '') ? eventId : createId('e', this.browser.crypto),
            type,
            timestamp: Date.now(),
            data,
        });
    }

    track(name, properties = {}, options = {}) {
        if (typeof name !== 'string' || name.trim() === '') {
            return;
        }

        const normalizedName = name.trim().slice(0, 100);
        const isFormActivity = options.category === 'form' && normalizedName === 'form_activity';
        const payload = {
            name: normalizedName,
            category: ['ecommerce', 'form'].includes(options.category) ? options.category : 'custom',
            properties: this.sanitizeTrackProperties(properties, isFormActivity),
        };

        if (this.pageViewId) {
            payload.page_view_id = this.pageViewId;
        }

        if (Number.isFinite(options.value)) {
            payload.value = Number(options.value);
        }

        if (typeof options.currency === 'string' && options.currency.trim() !== '') {
            payload.currency = options.currency.trim().slice(0, 3).toUpperCase();
        }

        if (Number.isFinite(options.quantity)) {
            payload.quantity = Math.max(0, Math.round(Number(options.quantity)));
        }

        this.enqueue('custom', payload, options.eventId);

        if (options.flush !== false) {
            this.flush({ preferBeacon: options.preferBeacon === true });
        }
    }

    identify(traits = {}, options = {}) {
        if (! traits || typeof traits !== 'object' || Array.isArray(traits)) {
            return;
        }

        this.track('identify', {
            user_id: traits.userId ?? traits.user_id ?? traits.externalUserId ?? traits.external_user_id,
            email: traits.email,
            customer_type: traits.customerType ?? traits.customer_type ?? traits.accountType ?? traits.account_type,
            customer_group: traits.customerGroup ?? traits.customer_group,
            preferred_currency: traits.preferredCurrency ?? traits.preferred_currency ?? traits.currency,
        }, {
            ...options,
            category: 'custom',
        });
    }

    productView(product = {}) {
        this.trackEcommerce('product_viewed', product, {
            value: product.price,
            currency: product.currency,
            quantity: product.quantity ?? 1,
        });
    }

    addToCart(product = {}, options = {}) {
        this.trackEcommerce('add_to_cart', product, {
            value: product.price,
            currency: product.currency,
            quantity: product.quantity ?? 1,
            flush: options.flush,
            preferBeacon: options.preferBeacon ?? true,
        });
    }

    removeFromCart(product = {}) {
        this.trackEcommerce('remove_from_cart', product, {
            value: product.price,
            currency: product.currency,
            quantity: product.quantity ?? 1,
        });
    }

    checkout(details = {}) {
        this.trackEcommerce('checkout_started', details, {
            value: details.total ?? details.value,
            currency: details.currency,
            quantity: details.quantity,
        });
    }

    purchase(details = {}) {
        this.trackEcommerce('purchase', {
            ...details,
            order_id: details.orderId ?? details.order_id,
            product_id: details.productId ?? details.product_id,
            product_name: details.name ?? details.product_name,
        }, {
            value: details.total ?? details.value,
            currency: details.currency,
            quantity: details.quantity,
        });
    }

    trackEcommerce(name, source = {}, options = {}) {
        const properties = this.sanitizeTrackProperties({
            product_id: source.productId ?? source.product_id,
            product_name: source.name ?? source.product_name,
            order_id: source.orderId ?? source.order_id,
            price: source.price,
            total: source.total,
            quantity: source.quantity,
            currency: source.currency,
            category: source.category,
            sku: source.sku,
            brand: source.brand,
            variant: source.variant,
        });

        this.track(name, properties, {
            category: 'ecommerce',
            value: options.value,
            currency: options.currency ?? source.currency,
            quantity: options.quantity ?? source.quantity,
            flush: options.flush,
            preferBeacon: options.preferBeacon === true,
        });
    }

    sanitizeTrackProperties(properties, allowsFormChanges = false) {
        if (! properties || typeof properties !== 'object' || Array.isArray(properties)) {
            return {};
        }

        const blocked = new Set([
            'authorization', 'auth', 'access_token', 'api_key', 'apikey', 'card', 'card_number',
            'cc', 'ccn', 'credit_card', 'cvv', 'cvc', 'expiry', 'id_token', 'jwt', 'pan',
            'password', 'passwd', 'payment_token', 'secret', 'session', 'session_id', 'ssn', 'token',
        ]);
        const sanitized = {};

        Object.keys(properties).slice(0, 40).forEach((key) => {
            if (typeof key !== 'string' || key === '' || key.length > 64 || blocked.has(key.toLowerCase())) {
                return;
            }

            const value = properties[key];

            if (allowsFormChanges && key === 'changes' && Array.isArray(value)) {
                sanitized[key] = value.slice(0, 200).map((change) => this.sanitizeFormChange(change)).filter(Boolean);

                return;
            }

            if (typeof value === 'string') {
                sanitized[key] = value.slice(0, 500);

                return;
            }

            if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
                sanitized[key] = value;
            }
        });

        return sanitized;
    }

    sanitizeFormChange(change) {
        if (! change || typeof change !== 'object' || Array.isArray(change)) {
            return null;
        }

        const allowed = new Set([
            'event', 'timestamp', 'form_id', 'field_name', 'field_type', 'error_type', 'file_count',
            'file_type', 'file_size', 'fields_completed', 'time_spent_seconds', 'last_field',
        ]);
        const sanitized = {};

        for (const [key, value] of Object.entries(change)) {
            if (! allowed.has(key)) {
                continue;
            }

            if (typeof value === 'string') {
                sanitized[key] = value.slice(0, 100);
            } else if (typeof value === 'number' || typeof value === 'boolean') {
                sanitized[key] = value;
            }
        }

        return typeof sanitized.event === 'string' && Number.isFinite(sanitized.timestamp) ? sanitized : null;
    }

    async flush({ preferBeacon = false } = {}) {
        if (this.softBlocked || this.flushInProgress || this.eventBuffer.count() === 0 || this.browser.navigator?.onLine === false) {
            return;
        }

        this.syncIdentityState();

        const items = this.eventBuffer.take(this.config.batchSize, this.visitorId, this.session.id);

        if (items.length === 0) {
            return;
        }

        // Keep journey history independent from page_resources so a bad resource
        // payload cannot block page_view / page_update ingestion.
        const journeyItems = items.filter((item) => item.event?.type !== 'page_resources');
        const resourceItems = items.filter((item) => item.event?.type === 'page_resources');

        this.flushInProgress = true;

        try {
            if (journeyItems.length > 0) {
                const journeyAccepted = await this.sendItems(journeyItems, preferBeacon);

                if (! journeyAccepted) {
                    this.eventBuffer.restore(resourceItems);

                    return;
                }
            }

            if (resourceItems.length > 0) {
                await this.sendItems(resourceItems, preferBeacon);
            }

            this.retryAttempt = 0;
        } finally {
            this.flushInProgress = false;

            if (! preferBeacon && this.eventBuffer.count() > 0 && this.retryTimer === null) {
                this.browser.setTimeout(() => this.flush(), 0);
            }
        }
    }

    scheduleFlush(delayMilliseconds = 3000, preferBeacon = false) {
        this.flushPreferBeacon = this.flushPreferBeacon || preferBeacon;

        if (this.flushTimer !== null) {
            return;
        }

        if (typeof this.browser.setTimeout !== 'function') {
            const useBeacon = this.flushPreferBeacon;
            this.flushPreferBeacon = false;
            this.flush({ preferBeacon: useBeacon });

            return;
        }

        this.flushTimer = this.browser.setTimeout(() => {
            const useBeacon = this.flushPreferBeacon;
            this.flushTimer = null;
            this.flushPreferBeacon = false;
            this.flush({ preferBeacon: useBeacon });
        }, Math.max(0, delayMilliseconds));
    }

    cancelScheduledFlush() {
        if (this.flushTimer !== null) {
            this.browser.clearTimeout?.(this.flushTimer);
            this.flushTimer = null;
        }

        this.flushPreferBeacon = false;
    }

    async sendItems(items, preferBeacon = false) {
        if (items.length === 0) {
            return true;
        }

        const payload = {
            store_id: this.config.storeId,
            visitor_id: this.visitorId,
            session_id: this.session.id,
            sdk_version: SDK_VERSION,
            events: items.map((item) => item.event),
        };

        try {
            await this.transport.send(payload, preferBeacon);

            return true;
        } catch (error) {
            if (error?.unrecoverable) {
                return true;
            }

            this.eventBuffer.restore(items);
            this.scheduleRetry();

            return false;
        }
    }

    scheduleRetry() {
        if (this.retryTimer !== null) {
            return;
        }

        const delay = Math.min(30000, 1000 * (2 ** this.retryAttempt)) + Math.floor(Math.random() * 250);
        this.retryAttempt = Math.min(this.retryAttempt + 1, 5);
        this.retryTimer = this.browser.setTimeout(() => {
            this.retryTimer = null;
            this.flush();
        }, delay);
    }

    captureScrollDepth() {
        this.engagement?.updateScrollDepth();
        this.maximumScrollDepth = this.engagement?.maxScrollPct ?? this.maximumScrollDepth;
    }

    engagementMetrics() {
        if (! this.engagement) {
            return null;
        }

        const metrics = this.engagement.snapshot();

        return Object.values(metrics).some((value) => value !== null && value !== false && value !== 0)
            ? metrics
            : null;
    }

    safely(callback) {
        try {
            return callback();
        } catch {
            return undefined;
        }
    }
}

function scriptSource(script) {
    return script?.getAttribute?.('src') || script?.src || '';
}

function resolveTrackerScript(browser) {
    const current = browser.document.currentScript;

    if (current && (current.dataset?.storeId || scriptSource(current).includes('tracker.js'))) {
        return current;
    }

    const withStoreId = [...(browser.document.querySelectorAll?.('script[data-store-id]') || [])];

    for (const script of withStoreId) {
        if (scriptSource(script).includes('tracker.js') || script.dataset?.apiUrl || script.dataset?.storeId) {
            return script;
        }
    }

    const bySrc = [...(browser.document.querySelectorAll?.('script[src*="tracker.js"]') || [])];

    return bySrc.at(-1) || withStoreId[0] || null;
}

function parseNetworkRequestPatterns(value) {
    const defaults = [/\/api(\/|$)/i];

    if (value === undefined || value === null || value === '') {
        return defaults;
    }

    const source = Array.isArray(value)
        ? value
        : String(value)
            .split(',')
            .map((pattern) => pattern.trim())
            .filter(Boolean);
    const patterns = [];

    for (const pattern of source) {
        try {
            if (pattern instanceof RegExp) {
                patterns.push(pattern);

                continue;
            }

            patterns.push(new RegExp(pattern, 'i'));
        } catch {
            // Ignore invalid custom patterns.
        }
    }

    return patterns.length > 0 ? patterns : defaults;
}

function booleanFlag(value, defaultValue) {
    if (value === undefined || value === null || value === '') {
        return defaultValue;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    const normalized = String(value).trim().toLowerCase();

    if (['false', '0', 'no', 'off'].includes(normalized)) {
        return false;
    }

    if (['true', '1', 'yes', 'on'].includes(normalized)) {
        return true;
    }

    return defaultValue;
}

function normalizeConfiguration(raw, browser, scriptUrl = browser.location.href) {
    if (! raw || typeof raw !== 'object') {
        return null;
    }

    const storeId = String(raw.storeId ?? raw.store_id ?? '').trim();

    if (storeId === '') {
        return null;
    }

    const sessionTimeoutMinutes = Number.parseInt(String(raw.sessionTimeoutMinutes ?? raw.sessionTimeout ?? raw.session_timeout ?? '30'), 10);
    const heartbeatMilliseconds = Number.parseInt(String(raw.heartbeatMilliseconds ?? raw.heartbeat ?? '60000'), 10);
    const apiUrl = String(raw.apiUrl ?? raw.api_url ?? '').trim()
        || new URL('api/v1/track/batch', scriptUrl).toString();
    const formConfigUrl = String(raw.formConfigUrl ?? raw.form_config_url ?? '').trim() || null;
    const rawBlockStatusUrl = raw.blockStatusUrl ?? raw.block_status_url;
    const blockStatusUrl = rawBlockStatusUrl === false
        ? null
        : String(rawBlockStatusUrl ?? '').trim() || deriveBlockStatusUrl(apiUrl, storeId, browser.location.href);

    return {
        storeId,
        apiUrl,
        formConfigUrl,
        blockStatusUrl,
        sessionTimeoutMinutes: Number.isFinite(sessionTimeoutMinutes) ? sessionTimeoutMinutes : 30,
        heartbeatMilliseconds: Number.isFinite(heartbeatMilliseconds) ? Math.max(15000, heartbeatMilliseconds) : 60000,
        batchSize: 40,
        consentMode: String(raw.consentMode ?? raw.consent ?? 'optional'),
        respectDoNotTrack: booleanFlag(raw.respectDoNotTrack ?? raw.respectDnt ?? raw.respect_dnt, true),
        disabled: booleanFlag(raw.disabled, false),
        trackSpaNavigation: booleanFlag(raw.trackSpaNavigation ?? raw.spa, true),
        trackJavascriptErrors: booleanFlag(raw.trackJavascriptErrors ?? raw.jsErrors ?? raw.js_errors, true),
        trackNetworkErrors: booleanFlag(raw.trackNetworkErrors ?? raw.networkErrors ?? raw.network_errors, true),
        trackNetworkRequests: booleanFlag(raw.trackNetworkRequests ?? raw.networkRequests ?? raw.network_requests, false),
        networkRequestPatterns: parseNetworkRequestPatterns(
            raw.networkRequestPatterns ?? raw.network_request_patterns ?? raw.apiPatterns ?? raw.api_patterns,
        ),
        trackPageResources: booleanFlag(raw.trackPageResources ?? raw.pageResources ?? raw.page_resources, true),
        trackPerformance: booleanFlag(raw.trackPerformance ?? raw.performance, true),
        trackButtonClicks: booleanFlag(raw.trackButtonClicks ?? raw.buttonClicks ?? raw.button_clicks, true),
        trackHeatmaps: booleanFlag(raw.trackHeatmaps ?? raw.heatmaps, true),
        sessionReplay: booleanFlag(raw.sessionReplay ?? raw.session_replay, false),
        replayApiUrl: String(raw.replayApiUrl ?? raw.replay_api_url ?? '').trim() || null,
        replayScriptUrl: String(raw.replayScriptUrl ?? raw.replay_script_url ?? '').trim() || null,
        debug: booleanFlag(raw.debug, false),
    };
}

function deriveBlockStatusUrl(apiUrl, storeId, baseUrl) {
    try {
        const url = new URL(apiUrl, baseUrl);
        url.pathname = url.pathname.replace(/\/batch\/?$/i, `/block-status/${encodeURIComponent(storeId)}`);
        url.search = '';

        return url.toString();
    } catch {
        return null;
    }
}

function configurationFromScript(browser) {
    const script = resolveTrackerScript(browser);

    if (! script) {
        return null;
    }

    const scriptUrl = scriptSource(script) || browser.location.href;
    const fromDataset = script.dataset?.storeId
        ? normalizeConfiguration({
            storeId: script.dataset.storeId,
            apiUrl: script.dataset.apiUrl,
            sessionTimeout: script.dataset.sessionTimeout,
            heartbeat: script.dataset.heartbeat,
            consent: script.dataset.consent,
            respectDnt: script.dataset.respectDnt,
            disabled: script.dataset.disabled,
            spa: script.dataset.spa,
            jsErrors: script.dataset.jsErrors,
            networkErrors: script.dataset.networkErrors,
            networkRequests: script.dataset.networkRequests,
            networkRequestPatterns: script.dataset.networkRequestPatterns,
            pageResources: script.dataset.pageResources,
            performance: script.dataset.performance,
            buttonClicks: script.dataset.buttonClicks,
            heatmaps: script.dataset.heatmaps,
            sessionReplay: script.dataset.sessionReplay,
            replayApiUrl: script.dataset.replayApiUrl,
            replayScriptUrl: script.dataset.replayScriptUrl,
            formConfigUrl: script.dataset.formConfigUrl,
            blockStatusUrl: script.dataset.blockStatusUrl,
            debug: script.dataset.debug,
        }, browser, scriptUrl)
        : null;

    if (fromDataset) {
        return fromDataset;
    }

    try {
        const url = new URL(scriptUrl, browser.location.href);

        return normalizeConfiguration({
            storeId: url.searchParams.get('store_id') || url.searchParams.get('storeId'),
            apiUrl: url.searchParams.get('api_url') || url.searchParams.get('apiUrl'),
            sessionTimeout: url.searchParams.get('session_timeout') || url.searchParams.get('sessionTimeout'),
            heartbeat: url.searchParams.get('heartbeat'),
            consent: url.searchParams.get('consent'),
            respectDnt: url.searchParams.get('respect_dnt') || url.searchParams.get('respectDnt'),
            disabled: url.searchParams.get('disabled'),
            spa: url.searchParams.get('spa'),
            jsErrors: url.searchParams.get('js_errors') || url.searchParams.get('jsErrors'),
            networkErrors: url.searchParams.get('network_errors') || url.searchParams.get('networkErrors'),
            networkRequests: url.searchParams.get('network_requests') || url.searchParams.get('networkRequests'),
            networkRequestPatterns: url.searchParams.get('network_request_patterns') || url.searchParams.get('networkRequestPatterns'),
            pageResources: url.searchParams.get('page_resources') || url.searchParams.get('pageResources'),
            performance: url.searchParams.get('performance'),
            buttonClicks: url.searchParams.get('button_clicks') || url.searchParams.get('buttonClicks'),
            heatmaps: url.searchParams.get('heatmaps'),
            sessionReplay: url.searchParams.get('session_replay') || url.searchParams.get('sessionReplay'),
            replayApiUrl: url.searchParams.get('replay_api_url') || url.searchParams.get('replayApiUrl'),
            replayScriptUrl: url.searchParams.get('replay_script_url') || url.searchParams.get('replayScriptUrl'),
            formConfigUrl: url.searchParams.get('form_config_url') || url.searchParams.get('formConfigUrl'),
            blockStatusUrl: url.searchParams.get('block_status_url') || url.searchParams.get('blockStatusUrl'),
            debug: url.searchParams.get('debug'),
        }, browser, scriptUrl);
    } catch {
        return null;
    }
}

function configurationFromGlobal(browser) {
    return normalizeConfiguration(browser.AILogTrackerConfig, browser);
}

function resolveConfiguration(browser, explicitConfig = null) {
    return normalizeConfiguration(explicitConfig, browser)
        || configurationFromGlobal(browser)
        || configurationFromScript(browser);
}

function bootstrapBlockReason(browser, config) {
    if (config.respectDoNotTrack && (browser.navigator?.doNotTrack === '1' || browser.navigator?.globalPrivacyControl === true)) {
        return 'Tracking is blocked because Do Not Track is enabled in your browser and respect_dnt is true.';
    }

    if (config.consentMode === 'required') {
        return 'Tracking is blocked because consent is required. Set localStorage.setItem("ailog:consent", "granted") or window.AILogConsent = { granted: true }.';
    }

    if (config.disabled) {
        return 'Tracking is disabled in the tracker configuration.';
    }

    return 'Tracking is blocked by the current privacy configuration.';
}

function publishBootstrapDiagnostics(browser, status, config = null) {
    if (browser.__ailogBootstrapDiagnosticsLogged || status === 'active' || status === 'initializing') {
        return;
    }

    browser.__ailogBootstrapDiagnosticsLogged = true;

    let message = null;

    if (status === 'waiting_for_config') {
        message = 'Tracker loaded but no store_id was found. Put store_id and api_url in the script src query string, add data-store-id, or set window.AILogTrackerConfig before loading tracker.js.';
    } else if (status === 'blocked' && config) {
        message = bootstrapBlockReason(browser, config);
    } else if (status === 'error') {
        message = 'Tracker failed to initialize. Check the browser console for JavaScript errors on the page.';
    }

    if (message) {
        browser.console?.warn?.(`[AILog] ${message}`);
    }
}

function publishTrackerApi(browser, client = null) {
    const api = {
        version: SDK_VERSION,
        status: browser.AILogTrackerStatus || 'idle',
        diagnostics() {
            const status = browser.AILogTrackerStatus || 'idle';
            const config = resolveConfiguration(browser);

            if (status === 'active') {
                return { status, message: 'Tracking is active.' };
            }

            if (status === 'waiting_for_config') {
                return {
                    status,
                    message: 'Missing store_id. Add store_id and api_url to the script src query string or set window.AILogTrackerConfig.',
                };
            }

            if (status === 'blocked' && config) {
                return { status, message: bootstrapBlockReason(browser, config) };
            }

            if (status === 'error') {
                return { status, message: 'Tracker initialization failed.' };
            }

            return { status, message: 'Tracker has not started yet.' };
        },
        init(config = null) {
            bootstrap(browser, config);

            return browser.AILogTracker;
        },
        track() {},
        identify() {},
        productView() {},
        addToCart() {},
        removeFromCart() {},
        checkout() {},
        purchase() {},
        formSuccess() {},
        formError() {},
        flush() {},
    };

    if (client) {
        api.visitorId = client.visitorId;
        api.sessionId = client.session.id;
        api.track = (name, properties, options) => client.safely(() => client.track(name, properties, options));
        api.identify = (traits, options) => client.safely(() => client.identify(traits, options));
        api.productView = (product) => client.safely(() => client.productView(product));
        api.addToCart = (product) => client.safely(() => client.addToCart(product));
        api.removeFromCart = (product) => client.safely(() => client.removeFromCart(product));
        api.checkout = (details) => client.safely(() => client.checkout(details));
        api.purchase = (details) => client.safely(() => client.purchase(details));
        api.formSuccess = (form) => client.safely(() => client.formTrackingManager.submissionSuccess(form));
        api.formError = (form, errorType) => client.safely(() => client.formTrackingManager.submissionError(form, errorType));
        api.flush = () => client.flush();
    }

    browser.AILogTracker = Object.freeze(api);
}

function scheduleBootstrapRetry(browser) {
    if (browser.__ailogBootstrapRetryScheduled) {
        return;
    }

    browser.__ailogBootstrapRetryScheduled = true;
    let attempts = 0;

    const retry = () => {
        if (browser.AILogTrackerStatus === 'active' || browser.AILogTrackerStatus === 'blocked') {
            return;
        }

        attempts += 1;
        bootstrap(browser);

        if (browser.AILogTrackerStatus === 'active' || browser.AILogTrackerStatus === 'blocked' || attempts >= 8) {
            return;
        }

        browser.setTimeout?.(retry, Math.min(500, 25 * attempts));
    };

    browser.setTimeout?.(retry, 0);
    browser.setTimeout?.(retry, 50);
    browser.setTimeout?.(retry, 200);
}

function bootstrap(browser, explicitConfig = null) {
    try {
        if (browser.__ailogTrackerClient) {
            if (browser.AILogTrackerStatus !== 'active') {
                browser.AILogTrackerStatus = 'active';
                publishTrackerApi(browser, browser.__ailogTrackerClient);
            }

            return browser.AILogTracker;
        }

        if (browser.AILogTrackerStatus === 'active' && browser.AILogTracker?.visitorId) {
            return browser.AILogTracker;
        }

        const config = resolveConfiguration(browser, explicitConfig);

        if (! config) {
            browser.AILogTrackerStatus = 'waiting_for_config';
            publishTrackerApi(browser);
            publishBootstrapDiagnostics(browser, 'waiting_for_config');
            scheduleBootstrapRetry(browser);

            return browser.AILogTracker;
        }

        if (! trackingIsAllowed(browser, config)) {
            browser.AILogTrackerStatus = 'blocked';
            publishTrackerApi(browser);
            publishBootstrapDiagnostics(browser, 'blocked', config);

            return browser.AILogTracker;
        }

        if (browser.AILogTrackerStatus === 'initializing') {
            return browser.AILogTracker;
        }

        browser.AILogTrackerStatus = 'initializing';

        const client = new TrackingClient(browser, config);
        browser.__ailogTrackerClient = client;
        client.start();
        browser.AILogTrackerStatus = 'active';
        publishTrackerApi(browser, client);

        return browser.AILogTracker;
    } catch {
        browser.AILogTrackerStatus = 'error';
        publishTrackerApi(browser);
        publishBootstrapDiagnostics(browser, 'error');

        return browser.AILogTracker;
    }
}

if (typeof window !== 'undefined') {
    bootstrap(window);
}

export {
    bootstrap,
    configurationFromGlobal,
    configurationFromScript,
    deriveBlockStatusUrl,
    normalizeConfiguration,
    resolveConfiguration,
    TrackingClient,
    TrackingTransport,
};
